// Follow-up tags — marks a coach puts on a member during or after a
// conversation, so the member can be found again when there's time to act on
// it. Deliberately separate from the intake fields: `privateTimes` records
// what the join form said months ago, while a "private" tag here means someone
// asked for one now and is waiting on an answer.
//
// Stored on the player as `followUps: ["session", "private"]`, and omitted
// entirely when nothing is tagged so untagged members stay byte-identical in
// kac_data.json.

export const FOLLOW_UPS = [
  {
    key: "session",
    label: "Wants another session",
    // Shown on the roster card, where there's room for about ten characters.
    chip: "+ session",
    // Tailwind classes for the roster filter chip when it's active.
    active: "bg-indigo-500 border-indigo-500 text-white",
    cardChip: "bg-indigo-100 text-indigo-700",
  },
  {
    key: "private",
    label: "Wants a private",
    chip: "1:1",
    active: "bg-teal-600 border-teal-600 text-white",
    cardChip: "bg-teal-100 text-teal-700",
  },
];

const VALID = new Set(FOLLOW_UPS.map(f => f.key));

// Every tag set on this player, ignoring anything not in the vocabulary above
// (a hand-edited kac_data.json or an older export can carry junk).
export function getFollowUps(player) {
  if (!Array.isArray(player?.followUps)) return [];
  return player.followUps.filter(t => VALID.has(t));
}

export function hasFollowUp(player, key) {
  return getFollowUps(player).includes(key);
}

// The `followUps` value a player should have once `key` is flipped. Returns
// undefined rather than [] when the last tag comes off, so `updatePlayer`
// drops the field instead of leaving an empty array behind.
export function toggleFollowUp(player, key) {
  if (!VALID.has(key)) return getFollowUps(player);
  const current = getFollowUps(player);
  const next = current.includes(key)
    ? current.filter(t => t !== key)
    // Keep the declared order rather than click order, so two members tagged
    // with the same pair serialize identically.
    : FOLLOW_UPS.map(f => f.key).filter(k => current.includes(k) || k === key);
  return next.length > 0 ? next : undefined;
}

export function countFollowUp(players, key) {
  return players.filter(p => hasFollowUp(p, key)).length;
}

// Members carrying at least one of `keys`. Used by the roster filter, where
// turning on both chips should widen the list to the whole follow-up queue
// rather than narrow it to members who happen to want both things.
export function matchesAnyFollowUp(player, keys) {
  if (!keys || keys.length === 0) return true;
  const tags = getFollowUps(player);
  return keys.some(k => tags.includes(k));
}
